import type { User, Organization } from '@awang/shared-types'
import { client } from './client'

const API_BASE = '/api/v1'

export interface RegisterRequest {
  email: string
  password: string
  displayName?: string
}

export interface LoginRequest {
  email: string
  password: string
}

export interface AuthResponse {
  user: User
  organization: Organization | null
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
}

async function authRequest<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const data = await response.json()
  if (!response.ok) {
    throw new Error(data.message || '请求失败')
  }
  // 兼容两种响应格式：直接返回数据 或 {success, data} 包装
  if (data.success !== undefined && data.data !== undefined) {
    return data.data as T
  }
  return data as T
}

export async function register(data: RegisterRequest): Promise<AuthResponse> {
  return authRequest<AuthResponse>('/identity/register', data)
}

export async function login(data: LoginRequest): Promise<AuthResponse> {
  return authRequest<AuthResponse>('/identity/login', data)
}

export async function getMe(): Promise<User> {
  return client.get<User>('/identity/me')
}

export async function logout(refreshToken: string): Promise<{ message: string }> {
  return client.post<{ message: string }>('/identity/logout', { refresh_token: refreshToken })
}

export async function createOrg(data: { name: string; code: string }): Promise<Organization> {
  return client.post<Organization>('/identity/orgs', data)
}

export interface InviteCreateRequest {
  email: string
  role?: string
}

export interface InviteResponse {
  id: string
  token: string
  email: string
  role: string
  expires_at: string
}

export async function createInvite(orgId: string, data: InviteCreateRequest): Promise<InviteResponse> {
  return client.post<InviteResponse>(`/identity/orgs/${orgId}/invite`, data)
}

export async function acceptInvite(token: string): Promise<{ message: string; organization: Organization }> {
  return client.post<{ message: string; organization: Organization }>(`/identity/invites/${token}/accept`, {})
}
